import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatApiError, rupiah, STATUS_LABELS } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { WhatsAppFloat } from "@/components/WhatsAppFloat";
import { Seo } from "@/components/Seo";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Star, Search, CheckCircle2, Clock } from "lucide-react";

const CATEGORIES = [
  { key: "timeliness", label: "Ketepatan Waktu" },
  { key: "cleanliness", label: "Kebersihan Kendaraan" },
  { key: "friendliness", label: "Keramahan Driver" },
  { key: "comfort", label: "Kenyamanan Perjalanan" },
  { key: "safety", label: "Keamanan Berkendara" },
  { key: "responsiveness", label: "Respons Pelayanan" },
];

const StarRow = ({ value, onChange, testid }) => (
  <div className="flex gap-1" data-testid={testid}>
    {[1, 2, 3, 4, 5].map((s) => (
      <button
        key={s}
        type="button"
        data-testid={`${testid}-${s}`}
        onClick={() => onChange(s)}
        className="transition-transform hover:scale-110"
        aria-label={`${s} bintang`}
      >
        <Star className={`h-6 w-6 ${s <= value ? "fill-primary text-primary" : "text-muted-foreground"}`} />
      </button>
    ))}
  </div>
);

export default function RatingPage() {
  const { orderNumber } = useParams();
  const [input, setInput] = useState(orderNumber || "");
  const [order, setOrder] = useState(null);
  const [checked, setChecked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [scores, setScores] = useState({ timeliness: 5, cleanliness: 5, friendliness: 5, comfort: 5, safety: 5, responsiveness: 5 });
  const [comment, setComment] = useState("");

  const fetchOrder = async (num) => {
    if (!num) return;
    setLoading(true);
    setChecked(false);
    try {
      const { data } = await api.get(`/orders/${num.trim().toUpperCase()}`);
      setOrder(data);
    } catch (e) {
      setOrder(null);
      toast.error("Nomor order tidak ditemukan");
    } finally {
      setChecked(true);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (orderNumber) fetchOrder(orderNumber);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderNumber]);

  const submit = async () => {
    setLoading(true);
    try {
      await api.post("/ratings", { order_id: order.order_number, ...scores, comment });
      setSubmitted(true);
      toast.success("Terima kasih atas ulasan Anda!");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Seo title="Beri Ulasan Perjalanan | Travele" description="Beri rating dan ulasan untuk perjalanan travel Anda di Travele." noindex />
      <Navbar />
      <div className="mx-auto max-w-2xl px-4 py-12 sm:px-6">
        <p className="font-mono-terminal text-xs uppercase tracking-widest text-primary">Ulasan Perjalanan</p>
        <h1 className="mt-2 font-display text-3xl font-black uppercase tracking-tighter sm:text-4xl">Beri Rating Perjalanan Anda</h1>
        <p className="mt-2 text-muted-foreground">Masukkan nomor order Anda untuk memberikan ulasan setelah perjalanan selesai.</p>

        <div className="mt-6 flex gap-2">
          <Input
            data-testid="rating-order-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Contoh: TRV-000001"
            onKeyDown={(e) => e.key === "Enter" && fetchOrder(input)}
          />
          <Button data-testid="rating-order-search" onClick={() => fetchOrder(input)} disabled={loading} className="bg-primary font-bold text-primary-foreground hover:bg-primary/90">
            <Search className="mr-1 h-4 w-4" /> Cek
          </Button>
        </div>

        {checked && order && (
          <div className="mt-8 border border-border bg-card p-6" data-testid="rating-order-detail">
            <div className="mb-4 border-b border-border pb-4 font-mono-terminal text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Order</span><span className="text-primary">{order.order_number}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Rute</span><span>{order.origin} → {order.destination}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Tanggal / Jam</span><span>{order.date} · {order.time}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Total</span><span>{rupiah(order.total)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Status</span><span>{STATUS_LABELS[order.status] || order.status}</span></div>
            </div>

            {submitted ? (
              <div className="text-center" data-testid="rating-thankyou">
                <CheckCircle2 className="mx-auto mb-3 h-14 w-14 text-emerald-400" />
                <h3 className="font-display text-xl font-bold">Ulasan Terkirim!</h3>
                <p className="mt-1 text-sm text-muted-foreground">Terima kasih telah membantu penumpang lain memilih travel terbaik.</p>
                <Link to="/"><Button variant="secondary" className="mt-4">Kembali ke Beranda</Button></Link>
              </div>
            ) : order.rated ? (
              <div className="text-center text-muted-foreground" data-testid="rating-already">
                <CheckCircle2 className="mx-auto mb-2 h-10 w-10 text-emerald-400" />
                Order ini sudah pernah diberi ulasan. Terima kasih!
              </div>
            ) : order.status !== "selesai" ? (
              <div className="text-center text-muted-foreground" data-testid="rating-not-done">
                <Clock className="mx-auto mb-2 h-10 w-10 text-amber-400" />
                Ulasan hanya dapat diberikan setelah perjalanan berstatus <b className="text-foreground">Selesai</b>.
                <br />Status saat ini: {STATUS_LABELS[order.status] || order.status}.
              </div>
            ) : (
              <div className="space-y-4" data-testid="rating-form">
                {CATEGORIES.map((c) => (
                  <div key={c.key} className="flex items-center justify-between">
                    <span className="text-sm">{c.label}</span>
                    <StarRow value={scores[c.key]} onChange={(v) => setScores({ ...scores, [c.key]: v })} testid={`stars-${c.key}`} />
                  </div>
                ))}
                <div className="grid gap-1.5 pt-2">
                  <Label>Ulasan (opsional)</Label>
                  <Textarea data-testid="rating-comment" value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Ceritakan pengalaman perjalanan Anda..." />
                </div>
                <Button data-testid="rating-submit" onClick={submit} disabled={loading} className="w-full bg-primary font-bold text-primary-foreground hover:bg-primary/90">
                  {loading ? "Mengirim..." : "Kirim Ulasan"}
                </Button>
              </div>
            )}
          </div>
        )}

        {checked && !order && (
          <p className="mt-6 border border-border bg-card p-6 text-center text-muted-foreground" data-testid="rating-notfound">
            Nomor order tidak ditemukan. Periksa kembali nomor order Anda.
          </p>
        )}
      </div>
      <Footer />
      <WhatsAppFloat />
    </div>
  );
}
